<template>
  <div class="call-result-comment">
    <h3 class="title">
      {{ $t('callResult.comment.text') }}
      <BBtn
        type="default"
        size="mini"
        outline
        class="insert-summarized-result-button"
        :disabled="!summarizedResult"
        @click="clickInsertSummarizedResult"
      >
        <BIcon
          size="mini"
          outlined
        >
          convert_to_text
        </BIcon>
        {{ $t('transcription.insertSummarizationText') }}
      </BBtn>
    </h3>
    <div class="input-container">
      <BInput
        v-model="comment"
        type="textarea"
        resize="none"
        :rows="null"
      />
    </div>
    <div class="button-container">
      <p
        v-if="showSfSyncNotice"
        class="sf-sync-notice"
      >
        {{ $t('todoSync.sfSyncNotice') }}
      </p>
      <BBtn
        type="primary"
        :loading="loading"
        :disabled="saveButtonDisabled"
        @click="clickSaveButton"
      >
        {{ $t('general.save.text') }}
      </BBtn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { CallResult } from '@/api/openapi';
import Api from '@/api/user';
import { CallResultApiService } from '@/api/user/resources/call_result';
import { useToast } from '@/composable/toast';
import { useSfTodoSyncNotice } from '@/composable/user/salesforce/use-sf-todo-sync-notice';
import { useWait } from '@/composable/vue-wait';
import SalesforceConst from '@/const/salesforce';

const props = defineProps<TProps>();
const emit = defineEmits<TEmits>();

type TProps = {
  callResult: CallResult | null;
  summarizedResult?: string;
};
type TEmits = {
  'update:comment': [string];
  'update:loading': [boolean];
}

const api = new CallResultApiService();
const { doActionWithWait, wait } = useWait();
const { successToast } = useToast();
const { t } = useI18n();
const { showSfSyncNotice } = useSfTodoSyncNotice('call_result');

const saveButtonDisabled = ref(false);
const comment = ref('');
const loading = computed(() => wait.is(['updateCallResultCommentWait']));

// 「連携済み」のTODOだけがSF更新の対象(未作成・失敗・連携中などはサーバー側でもスキップされる)
const isSfTaskSynced = computed(
  () => props.callResult?.sfRelatedLinkInfo?.sfRelatedLinks?.[0]?.status === SalesforceConst.Synced,
);

watch(loading, (newValue) => {
  emit('update:loading', newValue);
});

watch(() => props.callResult?.comment, (newValue) => {
  comment.value = newValue || '';
});

const clickSaveButton = async () => {
  const callResultId = props.callResult?.id;
  const callTargetId = props.callResult?.callTargetId;
  if (!callResultId) return;
  await doActionWithWait('updateCallResultCommentWait', async () => {
    await api.updateComment({
      request: {
        callResultId,
        patchCallResultCommentRequest: {
          comment: comment.value,
        },
      },
    });
    emit('update:comment', comment.value);

    // コメント更新後、SF連携済みの場合はSalesforceのTaskも更新する
    if (callTargetId && isSfTaskSynced.value) {
      try {
        await Api.updateSfTask({
          body: {
            type: 'call_result',
            targetId: callResultId,
            callTargetId,
          },
        });
      } catch {
        // SF更新失敗はコメント保存の成否に影響させない
      }
    }
  });
  successToast(t('general.save.doneOf', { item: t('callResult.comment.text') }));
};

const extractSummaryResult = (markdownText: string): string => {
  // 「## 要約結果」のセクションを探す（「概要」セクションを除く）
  // NOTE: プロンプトの内容に依存しているため、プロンプト変更時は対応が必要
  const summaryResultRegex = /(?:^|\n)##\s*要約結果\s*\n([\s\S]*?)(?=\n##|$)/;
  const match = markdownText.match(summaryResultRegex);

  if (match && match[1]) return match[1].trim();
  return '';
};
const clickInsertSummarizedResult = () => {
  if (!props.summarizedResult) return;
  
  const summaryResult = extractSummaryResult(props.summarizedResult);
  comment.value = comment.value ? `${comment.value}\n${summaryResult}` : summaryResult;
};
</script>

<style scoped lang="scss">
.call-result-comment {
  height: 100%;
  display: flex;
  flex-direction: column;

  .title {
    font-size: $fontsize-200;
    margin-bottom: $basespace-400;
    display: flex;
  }
  .input-container {
    flex-grow: 1;
    :deep(.b-input-wrapper) {
      height: 100%;
      .b-input-inner, .b-input, textarea {
        height: 100%;
      }
      textarea {
        min-height: 100% !important;
      }
    }
  }

  .button-container {
    @include m-flex-center;
    flex-direction: column;
    gap: $basespace-200;
    margin-top: $basespace-500;
  }
  .sf-sync-notice {
    font-size: $fontsize-100;
    color: $mars;
    text-align: center;
    margin: 0;
  }
  .insert-summarized-result-button {
    margin-left: auto;
    padding-left: $basespace-200;
    padding-right: $basespace-200;
    .b-icon {
      margin-right: $basespace-50;
    }
  }
}
</style>
